import { makeStyles } from 'lib/components';

export default makeStyles((theme) => ({
  // Matches Order mainContainer — positioning context for the absolute mobile drawer
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    position: 'relative',
    backgroundColor: theme.evPalette.salt[100],
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    '@media (min-width: 640px)': {
      padding: theme.spacing(3, 4),
    },
  },
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    width: '100%',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: '36px',
    color: theme.evPalette.charcoal[900],
    marginBottom: theme.spacing(1),
    '@media (min-width: 640px)': {
      fontSize: '32px',
      lineHeight: '40px',
    },
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: theme.evPalette.charcoal[700],
    marginBottom: theme.spacing(2),
  },
  metadataRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.evPalette.charcoal[300]}`,
    '@media (min-width: 640px)': {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(0, 1),
    },
  },
  metadataItem: {
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.evPalette.charcoal[800],
  },
  metadataLabel: {
    fontWeight: 600,
  },
  metadataDivider: {
    display: 'none',
    color: theme.evPalette.charcoal[500],
    '@media (min-width: 640px)': {
      display: 'inline',
    },
  },
  // Map + desktop sidebar row (Order selectionContainer)
  contentRow: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    width: '100%',
    minHeight: 420,
    '@media (min-width: 640px)': {
      flexDirection: 'row',
      gap: theme.spacing(3),
      alignItems: 'stretch',
      minHeight: 0,
    },
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    width: '100%',
    // Room for closed drawer peek on mobile (Order map fills; drawer overlays)
    paddingBottom: 56,
    '@media (min-width: 640px)': {
      flex: 3,
      paddingBottom: 0,
    },
  },
  selectAllRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: theme.evPalette.charcoal[100],
    borderRadius: 4,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 420,
    '@media (min-width: 640px)': {
      minHeight: 480,
    },
  },
  image: {
    maxWidth: '100%',
    maxHeight: '70vh',
    width: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  imageError: {
    padding: theme.spacing(3),
    color: theme.evPalette.rose[700] || theme.evPalette.red?.[700] || '#d32f2f',
    textAlign: 'center',
  },
  // Desktop sidebar
  sidebarDesktop: {
    display: 'none',
    '@media (min-width: 640px)': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 280,
      maxWidth: 360,
      border: `1px solid ${theme.evPalette.charcoal[300]}`,
      borderRadius: 4,
      padding: theme.spacing(2),
      boxSizing: 'border-box',
      backgroundColor: theme.evPalette.salt[100],
    },
  },
  // Mobile drawer — Order productPanelMobile pattern (absolute inside page, not fixed)
  sidebarMobile: {
    width: '100%',
    position: 'absolute',
    left: 0,
    right: 0,
    backgroundColor: theme.evPalette.salt[100],
    minHeight: 0,
    display: 'flex',
    paddingTop: '10px',
    flexDirection: 'column',
    transitionDuration: '415ms',
    transition: 'all 500ms linear',
    borderRadius: '10px 10px 0 0',
    boxSizing: 'border-box',
    '@media (min-width: 640px)': {
      display: 'none',
    },
  },
  sidebarMobileClosed: {
    top: '94%',
    height: '6%',
    overflow: 'hidden',
    zIndex: 99,
  },
  sidebarMobileOpen: {
    top: '0%',
    height: '100%',
    overflow: 'auto',
    zIndex: 199, // header sticky z-index is 200 — stays above drawer
    padding: theme.spacing(0, 2, 2),
  },
  toggleDrawerButton: {
    color: theme.evPalette.ocean[600],
    textTransform: 'none',
    padding: 0,
    height: 40,
    fontWeight: 600,
    fontSize: 16,
    flexShrink: 0,
    '& .myev-MuiButton-label': {
      height: 40,
    },
  },
  toggleDrawerArrow: {
    margin: '0 8px',
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '28px',
    color: theme.evPalette.charcoal[900],
    marginBottom: theme.spacing(0.5),
  },
  sidebarCount: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.evPalette.charcoal[700],
    marginBottom: theme.spacing(1.5),
  },
  emptySelection: {
    fontSize: '14px',
    color: theme.evPalette.charcoal[700],
    marginBottom: theme.spacing(2),
  },
  infoBox: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    backgroundColor: theme.evPalette.ocean[100],
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    flexShrink: 0,
  },
  infoIcon: {
    color: theme.evPalette.ocean[600],
    fontSize: 20,
    flexShrink: 0,
    marginTop: 2,
  },
  infoText: {
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.evPalette.charcoal[900],
  },
  contactLink: {
    color: theme.evPalette.ocean[700],
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  structureList: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    marginBottom: theme.spacing(2),
  },
  sidebarFooter: {
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.evPalette.charcoal[300]}`,
    flexShrink: 0,
  },
  measurementRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  measurementLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.evPalette.charcoal[900],
  },
  measurementCount: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.evPalette.charcoal[900],
  },
  submitButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 16,
    height: 48,
  },
  submitHint: {
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.evPalette.charcoal[700],
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.evPalette.rose[700] || '#d32f2f',
    marginBottom: theme.spacing(1),
  },
}));
